import React, { useEffect, useState, useMemo } from "react";
import PropTypes from "prop-types";
import { Grid, TextField, InputAdornment, IconButton } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import "../../assets/css/competition.scss";
import Title from "../ReusableComponents/title";
import CombinedPopup from "../../components/Popups/CombinedPopup";
import { country as countryList } from "../../utilities/country";
import Select from "react-select";
import { customStyles } from "../../reusableComponents/MobileTabs/customstyle";
import { useDispatch, useSelector } from "react-redux";
import { axiosInstance } from "../../Axios";
import { getUserProfile } from "../../store/reducers/userProfileReducer";
import { message } from "antd";

// Yup validation schema
const validationSchema = Yup.object().shape({
  first_name: Yup.string()
    .required("First name is required")
    .test(
      "no-leading-trailing-spaces",
      "Name cannot start or end with spaces",
      (value) => value === value?.trim()
    )
    .test(
      "no-consecutive-spaces",
      "Name cannot contain consecutive spaces",
      (value) => !value || !/\s{2,}/.test(value)
    )
    .matches(
      /^[\p{L}\p{M}\s'-]+$/u,
      "Only letters, spaces, hyphens, and apostrophes are allowed"
    )
    .min(1, "Must be at least 1 character")
    .max(70, "Must be at most 70 characters"),
  last_name: Yup.string()
    .required("Last name is required")
    .test(
      "no-leading-trailing-spaces",
      "Name cannot start or end with spaces",
      (value) => value === value?.trim()
    )
    .test(
      "no-consecutive-spaces",
      "Name cannot contain consecutive spaces",
      (value) => !value || !/\s{2,}/.test(value)
    )
    .matches(
      /^[\p{L}\p{M}\s'-]+$/u,
      "Only letters, spaces, hyphens, and apostrophes are allowed"
    )
    .min(1, "Must be at least 1 character")
    .max(70, "Must be at most 70 characters"),

  password: Yup.string()
    .required("Password is required")
    .matches(/^[^\s]+$/, "Password cannot contain spaces"),
  competition_password: Yup.string().when("isCommunity", {
    is: true,
    then: (schema) =>
      schema
        .required("Community password is required")
        .matches(/^[^\s]+$/, "Community password cannot contain spaces"),
    otherwise: (schema) => schema.notRequired(),
  }),
});

const CompDetForm = ({ isCommunity, onSubmitChange }) => {
  console.log(isCommunity, "isCommunityisCommunity");
  const navigate = useNavigate();
  const user_profile = useSelector((state) => state.user);
  const ACCtoken = useSelector((state) => state.ACCtoken);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showCommunityPassword, setShowCommunityPassword] = useState(false);
  const [serverErrors, setServerErrors] = useState({});
  const [profileComplete, setProfileComplete] = useState(true);

  const dispatch = useDispatch();
  const [showPopup, setShowPopup] = useState(false);
  const [popupConfig, setPopupConfig] = useState({
    header: "",
    body: "",
    type: "",
    onClose: null,
  });

  const [disableFields, setDisableFields] = useState({
    first_name: false,
    last_name: false,
    password: false,
    competition_password: false, // if applicable
  });
  console.log(user_profile, "user_profilemahi");

  // Check if profile is complete
  useEffect(() => {
    const requiredFields = [
      "first_name",
      "last_name",
      "email",
      "Contact_number",
      "country_code",
      "Country",
      "City",
    ];

    const fieldStatus = {};
    const isComplete = requiredFields.every((field) => {
      const value = user_profile[field];
      const isValid =
        value !== null &&
        value !== undefined &&
        (typeof value === "string" ? value.trim() !== "" : true);

      fieldStatus[field] = { value, isValid };
      return isValid;
    });

    if (!isComplete) {
      setProfileComplete(false);
      navigate("/profile", {
        state: {
          message:
            "Please complete your profile information before proceeding.",
        },
        replace: true,
      });
      message.error(
        "Please complete your profile information before proceeding."
      );
    } else {
      setProfileComplete(true);
    }
  }, [user_profile, navigate]);

  // Initial form values
  const initialValues = useMemo(
    () => ({
      first_name: user_profile.first_name || "",
      nickname: user_profile.nickname || "",
      last_name: user_profile.last_name || "",
      password: "",
      competition_password: "",
    }),
    [user_profile]
  );

  useEffect(() => {
    console.log(user_profile, "user_profile");

    // Check each field and match with user_profile to determine if it should be disabled
    const newDisableFields = {};

    Object.keys(initialValues).forEach((key) => {
      const userValue = user_profile[key];

      // Check if the value exists and if it's not an empty string or zero (for numbers)
      if (
        userValue &&
        (typeof userValue === "string" ? userValue.length > 0 : userValue !== 0)
      ) {
        newDisableFields[key] = true; // Disable the field if the value is non-empty or non-zero
      } else {
        newDisableFields[key] = false; // Keep the field enabled if the value is empty or zero
      }
    });

    setDisableFields(newDisableFields); // Update state with disable field status
  }, [user_profile, initialValues]); // Dependency on user_profile and initialValues

  console.log(disableFields, "disableFields");

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleToggleCommunityPasswordVisibility = () => {
    setShowCommunityPassword(!showCommunityPassword);
  };

  const handleSubmit = async (values, { setSubmitting, setFieldError }) => {
    setIsLoading(true);
    setServerErrors({}); // Clear previous server errors

    const syntheticEvent = {
      preventDefault: () => {},
      stopPropagation: () => {},
    };
    try {
      const response = await onSubmitChange(syntheticEvent, {
        name: values.first_name + values?.last_name,
        pwd: values.password,
        nickname: values.nickname,
        competition_password: values.competition_password,
      });
      console.log("Competition registration response:", response);
    } catch (error) {
      console.error("Error during submission:", error);
      console.error("Error stack:", error.stack);
      // message.error(
      //   error.error || error.response.detail || "Something went wrong!"
      // );
    } finally {
      console.log("Cleaning up loading states");
      setSubmitting(false);
      setIsLoading(false);
      dispatch(getUserProfile());
    }
  };

  return (
    <Grid container item lg={8} sm={12}>
      <CombinedPopup
        open={showPopup}
        handleClose={() => setShowPopup(false)}
        header={popupConfig.header}
        body={popupConfig.body}
        type={popupConfig.type}
      />
      <Grid item container>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize={true}
        >
          {({ values, errors, touched, setFieldValue }) => (
            <Form
              style={{
                width: "100%",
                border: "1px solid #8B8E93",
                borderRadius: "12px",
                height: "fit-content",
              }}
              autoComplete="off"
              className="form_div"
            >
              <Grid
                container
                spacing={2}
                style={{ padding: "1rem" }}
                className="form_innerWrapper"
              >
                {[
                  { label: "Nickname", field: "nickname", type: "text" },
                  { label: "First Name", field: "first_name", type: "text" },
                  { label: "Last Name", field: "last_name", type: "text" },

                  {
                    label: "Password",
                    field: "password",
                    type: "password",
                    custom: (
                      <div className="password-field">
                        <Field name="password">
                          {({ field }) => (
                            <TextField
                              {...field}
                              fullWidth
                              sx={{ padding: 0 }}
                              type={showPassword ? "text" : "password"}
                              disabled={disableFields?.password}
                              error={
                                (touched.password &&
                                  Boolean(errors.password)) ||
                                Boolean(serverErrors.password)
                              }
                              helperText={
                                (touched.password && errors.password) ||
                                serverErrors.password
                              }
                              autoComplete="new-password" // change for password field
                              InputProps={{
                                endAdornment: (
                                  <InputAdornment position="end">
                                    <IconButton
                                      onClick={handleTogglePasswordVisibility}
                                      edge="end"
                                      disabled={disableFields?.password}
                                      size="small"
                                      sx={{
                                        position: "absolute",
                                        right: "20px",
                                        top: "50%",
                                        transform: "translateY(-50%)",
                                        zIndex: 1,
                                        color: "#fff",
                                      }}
                                    >
                                      {!showPassword ? (
                                        <VisibilityOff />
                                      ) : (
                                        <Visibility />
                                      )}
                                    </IconButton>
                                  </InputAdornment>
                                ),
                              }}
                            />
                          )}
                        </Field>
                      </div>
                    ),
                  },

                  ...(isCommunity
                    ? [
                        {
                          label: "Community Password",
                          field: "communityPassword",
                          type: "password",
                          custom: (
                            <div className="password-field">
                              <Field name="competition_password">
                                {({ field }) => (
                                  <TextField
                                    {...field}
                                    fullWidth
                                    type={
                                      showCommunityPassword
                                        ? "text"
                                        : "password"
                                    }
                                    disabled={
                                      disableFields?.competition_password
                                    }
                                    error={
                                      (touched.competition_password &&
                                        Boolean(errors.competition_password)) ||
                                      Boolean(serverErrors.competition_password)
                                    }
                                    helperText={
                                      (touched.competition_password &&
                                        errors.competition_password) ||
                                      serverErrors.competition_password
                                    }
                                    autoComplete="new-community-password" // unique value
                                    InputProps={{
                                      endAdornment: (
                                        <InputAdornment position="end">
                                          <IconButton
                                            onClick={
                                              handleToggleCommunityPasswordVisibility
                                            }
                                            edge="end"
                                            disabled={
                                              disableFields?.competition_password
                                            }
                                            size="small"
                                            sx={{
                                              position: "absolute",
                                              right: "8px",
                                              top: "50%",
                                              transform: "translateY(-50%)",
                                              zIndex: 1,
                                              color: "#fff",
                                            }}
                                          >
                                            {showCommunityPassword ? (
                                              <VisibilityOff />
                                            ) : (
                                              <Visibility />
                                            )}
                                          </IconButton>
                                        </InputAdornment>
                                      ),
                                    }}
                                  />
                                )}
                              </Field>
                            </div>
                          ),
                        },
                      ]
                    : []),
                ].map((input, index) => (
                  <Grid key={index} item lg={6} sm={12}>
                    <Title three>{input.label}</Title>
                    {input.custom || (
                      <Field name={input.field}>
                        {({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            type={input.type || "text"}
                            disabled={disableFields[input?.field]}
                            error={
                              (touched[input.field] &&
                                Boolean(errors[input.field])) ||
                              Boolean(serverErrors[input.field])
                            }
                            helperText={
                              (touched[input.field] && errors[input.field]) ||
                              serverErrors[input.field]
                            }
                            autoComplete={`new-${input.field}`} // unique string for each field
                          />
                        )}
                      </Field>
                    )}
                  </Grid>
                ))}
              </Grid>
              <button
                className="Button"
                type="submit"
                style={{
                  maxWidth: "390px",
                  width: "100%",
                  marginBottom: "1rem",
                }}
                disabled={isLoading} // Disable button while is_loading
              >
                {isLoading ? "Submitting..." : "Submit"}
              </button>
            </Form>
          )}
        </Formik>
      </Grid>
    </Grid>
  );
};

CompDetForm.propTypes = {
  isCommunity: PropTypes.bool,
  onSubmitChange: PropTypes.func.isRequired,
};

export default CompDetForm;
